import type { CtaType } from '@ria-client/segment/Analytics/types';
import type { LinkType, MonetizableEnum } from '@ria-client/segment/enums';
import type { AnalyticsType } from '@ria-client/segment/types';
import type * as React from 'react';
import { ClientTrackedLink } from './ClientTrackedLink';

/**
 * CTA-owned link attributes cannot be overridden because they preserve tracking,
 * attribution, and the destination's navigation policy.
 */
export interface TrackedLinkProps extends Omit<
  React.ComponentProps<'a'>,
  'href' | 'onClick' | 'rel' | 'target'
> {
  /** Destination and label, as the content layer supplies them. */
  cta: CtaType;
  /** Monetization is passed separately because a caller may override it. */
  analyticsPayload: Omit<AnalyticsType, 'monetizable'>;
  linkType: LinkType;
  monetizable: MonetizableEnum;
}

/**
 * Mints the shopping-finish id, then hands off to the client component.
 *
 * The id is a dash-stripped uuid because downstream Shopping Finished
 * correlation depends on that format; `useId` would change it.
 *
 * This file carries no `'use client'` directive, matching
 * packages/segment/src/components/CtaLink/index.tsx. That does NOT make it
 * server-only: every consumer reaches it through CtaButton, which is a client
 * component, so in practice the id is minted in the client graph. Hydration is
 * still safe — `useGetCtaLink` seeds its state with the bare `cta.link` and only
 * appends the id inside an effect (useGetCtaLink.ts:44,47,82), so the id never
 * reaches the first render's DOM and server and client HTML agree.
 *
 * A re-render mints a fresh id while `useGetCtaLink`'s effect keeps the first
 * one (its deps omit the id), so the URL's `shopping_finish_id` and the click
 * handler's can drift apart. Harmless in both directions: the id is only
 * appended to the URL for a monetizing clickout, which fires no event here, and
 * only sent in an event for a non-monetizing one, which appends no params.
 * Legacy CtaLink behaves identically.
 */
export function TrackedLink(props: TrackedLinkProps): React.JSX.Element {
  const shoppingFinishedId = crypto.randomUUID().replace(/-/g, '');

  return <ClientTrackedLink shoppingFinishedId={shoppingFinishedId} {...props} />;
}
